import { toJS } from 'mobx';
import { cast, types, getRoot } from 'mobx-state-tree';
import moment from 'moment';
import { BillingSummary, DateRange } from './allocations';
import { SubmeterBillFinalStateModel } from './property/allocations';
const ApplyBill = types.model({
  allocationCycleId: types.string,
  allocationMethod: types.string,
  billingCycleId: types.string,
  buildingAddress: types.maybeNull(types.string),
  commonAreaDeduction: types.maybeNull(types.number),
  cycleName: types.string,
  goodWillDeduction: types.maybeNull(types.number),
  propertyId: types.maybeNull(types.string),
  status: types.string,
  utilityId: types.string,
  utilityName: types.string
});
const PreviewCycleCharges = types.model('PreviewCycleCharges', {
  allocationCycleId: types.string,
  cycleName: types.string,
  buildingAddress: types.maybeNull(types.string),
  propertyId: types.string
});
const SkipModal = types.model('SkipModal', {
  additionalSkipComment: types.maybeNull(types.string),
  allocationCycleId: types.string,
  allocationTableSkipReason: types.maybeNull(types.string),
  billingCycleId: types.string,
  buildingAddress: types.maybeNull(types.string),
  cycleName: types.string,
  isEdit: types.boolean,
  isFromHistory: types.maybeNull(types.boolean),
  otherSkipReason: types.maybeNull(types.string),
  propertyId: types.maybeNull(types.string),
  utilityId: types.string,
  utilityName: types.string
});
const CustomUtilities = types.model('CustomUtilities', {
  billingCycleId: types.maybeNull(types.string),
  utilityId: types.string,
  utilityName: types.string
});
const CustomTable = types.model('CustomTable', {
  allocationCycleId: types.maybeNull(types.string),
  propertyId: types.maybeNull(types.string),
  utilities: types.array(CustomUtilities)
});
const SubmeterBillModel = types.model('SubmeterBillModel', {
  allocationCycleId: types.string,
  allocationMethod: types.string,
  billingCycleId: types.string,
  buildingAddress: types.maybeNull(types.string),
  cycleName: types.string,
  goodWillDeduction: types.maybeNull(types.number),
  propertyId: types.maybeNull(types.string),
  status: types.string,
  utilityId: types.string,
  utilityName: types.string,
  submeterBillFinalStateData: types.maybeNull(types.array(SubmeterBillFinalStateModel))
}).actions(store => {
  return {
    updateSubmeterBillFinalStateData(data) {
      store.submeterBillFinalStateData = data;
    }
  };
});
const PreviewQueue = types.model('PreviewQueue', {
  allocationCycleId: types.maybeNull(types.string),
  billingCycleId: types.string,
  id: types.string,
  portfolioId: types.string,
  propertyId: types.string,
  submeterBillFinalStateData: types.maybeNull(types.array(SubmeterBillFinalStateModel))
});
const BillingTabModel = types.model('BillingTabModel', {
  allocationBatchIdValue: types.optional(types.string, ''),
  allocationCycleIsLoading: types.map(types.boolean),
  applyBillValue: types.optional(ApplyBill, {
    allocationCycleId: '',
    allocationMethod: '',
    billingCycleId: '',
    cycleName: '',
    status: '',
    utilityId: '',
    utilityName: ''
  }),
  applyBillAutosaveValue: types.maybeNull(types.boolean),
  billingSummaryTimestampValue: types.optional(types.number, 0),
  billingSummaryValue: types.maybeNull(types.array(BillingSummary)),
  customTableValue: types.optional(CustomTable, {
    allocationCycleId: null,
    propertyId: null,
    utilities: []
  }),
  customTableUtilityIdValue: types.maybeNull(types.string),
  dateRangeValue: types.optional(DateRange, {
    endDateOnly: null,
    startDateOnly: null
  }),
  filterStatusesValue: types.optional(types.array(types.string), ['all']),
  submeterBillModalOpenState: types.optional(types.boolean, false),
  isApplyBillingIsOpenValue: types.optional(types.boolean, false),
  isAttachBillFileModalOpenValue: types.optional(types.string, ''),
  isFileChosenValue: types.optional(types.boolean, false),
  isBillEditValue: types.optional(types.boolean, false),
  isModalLoadingValue: types.optional(types.boolean, false),
  isCustomTableOpenValue: types.maybeNull(types.boolean),
  isPreviewCycleChargesModalOpenValue: types.optional(types.boolean, false),
  isSkipModalOpenValue: types.optional(types.boolean, false),
  previewCycleChargesValue: types.optional(PreviewCycleCharges, {
    allocationCycleId: '',
    cycleName: '',
    propertyId: ''
  }),
  previewQueueValues: types.array(PreviewQueue),
  skipModalValue: types.optional(SkipModal, {
    allocationCycleId: '',
    billingCycleId: '',
    cycleName: '',
    isEdit: false,
    isFromHistory: null,
    propertyId: null,
    utilityId: '',
    utilityName: ''
  }),
  statusValue: types.optional(types.string, 'all'),
  submeterBillValue: types.optional(SubmeterBillModel, {
    allocationCycleId: '',
    allocationMethod: '',
    billingCycleId: '',
    cycleName: '',
    status: '',
    utilityId: '',
    utilityName: '',
    propertyId: null,
    submeterBillFinalStateData: null
  })
}).actions(store => {
  return {
    addToPreviewQueue(data) {
      try {
        if (!store.previewQueueValues.find(item => item.id === data.id)) store.previewQueueValues.push(data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    clearPreviewCycleCharges() {
      try {
        store.previewCycleChargesValue = {
          allocationCycleId: '',
          cycleName: '',
          buildingAddress: null,
          propertyId: ''
        };
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    clearSkipModal() {
      try {
        store.skipModalValue = {
          allocationCycleId: '',
          additionalSkipComment: null,
          allocationTableSkipReason: null,
          billingCycleId: '',
          buildingAddress: null,
          cycleName: '',
          isEdit: false,
          isFromHistory: null,
          otherSkipReason: null,
          propertyId: null,
          utilityId: '',
          utilityName: ''
        };
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    removeFromPreviewQueue(data) {
      try {
        store.previewQueueValues = cast(store.previewQueueValues.filter(item => item.id !== data.id));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setAllocationBatchIdAfterFetch(data) {
      try {
        store.allocationBatchIdValue = data.response.allocationBatchId;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setAllocationCycleIsLoadingById(id, flag) {
      try {
        store.allocationCycleIsLoading.set(id, flag);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setApplyBill(data) {
      try {
        store.applyBillValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setApplyBillAutosave(value) {
      try {
        store.applyBillAutosaveValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setBillingSummaryAfteFetch(data) {
      try {
        store.billingSummaryValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCustomTable(data) {
      try {
        store.customTableValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCustomTableUtilityId(value) {
      try {
        store.customTableUtilityIdValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCustomTableIsOpen(value) {
      try {
        store.isCustomTableOpenValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setDateRange(data) {
      try {
        store.dateRangeValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsApplyBillingIsOpen(value) {
      try {
        store.isApplyBillingIsOpenValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsAttachBillFileModalOpen(value) {
      try {
        store.isAttachBillFileModalOpenValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsFileChosen(value) {
      try {
        store.isFileChosenValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setSubmeterBillModalOpenState(value) {
      try {
        store.submeterBillModalOpenState = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    fetchSubmeterBillFinalStateModal(data) {
      const _ref = data !== null && data !== void 0 ? data : store.submeterBillValue,
        propertyId = _ref.propertyId,
        utilityId = _ref.utilityId,
        billingCycleId = _ref.billingCycleId;
      return store.fetchSubmeterLastBillBillState({
        propertyId,
        utilityId,
        billingCycleId
      }).then(resp => {
        if (resp !== null && resp !== void 0 && resp.isSuccessful) {
          store.submeterBillValue.updateSubmeterBillFinalStateData(resp.response);
        }
      });
    },
    setSubmeterBill(data) {
      try {
        store.submeterBillValue = data;
        return store.fetchSubmeterBillFinalStateModal(data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    fetchSubmeterLastBillBillState(_ref2) {
      var _getRoot;
      let propertyId = _ref2.propertyId,
        utilityId = _ref2.utilityId,
        billingCycleId = _ref2.billingCycleId;
      return (_getRoot = getRoot(store)) === null || _getRoot === void 0 ? void 0 : _getRoot.allocations.postGetSubMeterFinalStateAsync.run({
        propertyId: propertyId,
        utilityId: utilityId,
        billingCycleId: billingCycleId
      }, [utilityId, billingCycleId, propertyId]);
    },
    setFilterStatuses(value) {
      try {
        store.filterStatusesValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsBillEdit(value) {
      try {
        store.isBillEditValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsModalLoading(value) {
      try {
        store.isModalLoadingValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsPreviewCycleChargesModalOpen(value) {
      try {
        store.isPreviewCycleChargesModalOpenValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setPreviewCycleCharges(data) {
      try {
        store.previewCycleChargesValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setSkipModalOpen(value) {
      try {
        store.isSkipModalOpenValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setSkipModal(data) {
      try {
        store.skipModalValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setStatus(value) {
      try {
        store.statusValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    updateBillingSummaryTimestamp() {
      try {
        store.billingSummaryTimestampValue = moment().unix();
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => ({
  get allocationBatchId() {
    return store.allocationBatchIdValue;
  },
  get applyBill() {
    return store.applyBillValue;
  },
  get applyBillAutosave() {
    return store.applyBillAutosaveValue;
  },
  get billingSummary() {
    return store.billingSummaryValue;
  },
  get billingSummaryTimestamp() {
    return store.billingSummaryTimestampValue;
  },
  get customTable() {
    return store.customTableValue;
  },
  get customTableUtilityId() {
    return store.customTableUtilityIdValue;
  },
  get dateRange() {
    return store.dateRangeValue;
  },
  get filterStatuses() {
    return store.filterStatusesValue;
  },
  get isApplyBillingIsOpen() {
    return store.isApplyBillingIsOpenValue;
  },
  get isAttachBillFileModalOpen() {
    return store.isAttachBillFileModalOpenValue;
  },
  get isFileChosen() {
    return store.isFileChosenValue;
  },
  get isSubmeterBillModalOpen() {
    return store.submeterBillModalOpenState;
  },
  get submeterBill() {
    return store.submeterBillValue;
  },
  get isBillEdit() {
    return store.isBillEditValue;
  },
  get isModalLoading() {
    return store.isModalLoadingValue;
  },
  get isCustomTableOpen() {
    return store.isCustomTableOpenValue;
  },
  get isPreviewCycleChargesModalOpen() {
    return store.isPreviewCycleChargesModalOpenValue;
  },
  get isSkipModalOpen() {
    return store.isSkipModalOpenValue;
  },
  get previewCycleCharges() {
    return store.previewCycleChargesValue;
  },
  get previewQueue() {
    return toJS(store.previewQueueValues);
  },
  get skipModal() {
    return store.skipModalValue;
  },
  get status() {
    return store.statusValue;
  }
}));
export default BillingTabModel;